import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Info, AlertTriangle, AlertOctagon, X, Bell } from "lucide-react";

const SEV_STYLES = {
  info: { icon: Info, badge: "bg-[#4A6FA5]/10 text-[#2A4B7A] border-[#4A6FA5]/40", banner: "bg-[#4A6FA5]/10 border-[#4A6FA5]/40 text-[#2A4B7A]", ring: "text-[#4A6FA5]" },
  warning: { icon: AlertTriangle, badge: "bg-[#E76F51]/15 text-[#B04329] border-[#E76F51]/40", banner: "bg-[#FDF1EC] border-[#E76F51]/40 text-[#8B2E1A]", ring: "text-[#B04329]" },
  critical: { icon: AlertOctagon, badge: "bg-[#C84B31] text-white border-[#C84B31]", banner: "bg-[#C84B31] text-white border-[#8B2E1A]", ring: "text-white" },
};

// ── Bildirim izni + ses uyarısı yardımcıları ─────────────────────────
const NOTIF_PREF_KEY = "kh_notif_pref"; // 'granted' | 'denied' | 'dismissed'

const canNotify = () => typeof window !== "undefined" && "Notification" in window;

const playBeep = (severity = "info") => {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    // Kritik = 3'lü uyarı sesi, Uyarı = 2, Info = 1
    const beeps = severity === "critical" ? 3 : severity === "warning" ? 2 : 1;
    const freq = severity === "critical" ? 880 : severity === "warning" ? 660 : 520;
    osc.type = "sine";
    osc.frequency.setValueAtTime(freq, ctx.currentTime);
    const step = 0.18;
    const dur = 0.12;
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    for (let i = 0; i < beeps; i++) {
      const t = ctx.currentTime + i * step;
      gain.gain.exponentialRampToValueAtTime(0.35, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    }
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + beeps * step + 0.05);
    setTimeout(() => { try { ctx.close(); } catch (_) {} }, (beeps * step + 0.2) * 1000);
  } catch (_) { /* sessiz */ }
};

const showBrowserNotification = (a) => {
  if (!canNotify()) return null;
  if (Notification.permission !== "granted") return null;
  try {
    const n = new Notification(a.title || "Yeni Duyuru", {
      body: a.body || "",
      icon: "/logo192.png",
      tag: `ann-${a.id}`,
      requireInteraction: a.severity === "critical",
      silent: false,
    });
    n.onclick = () => { try { window.focus(); n.close(); } catch (_) {} };
    return n;
  } catch (_) { return null; }
};

export default function AnnouncementViewer() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [popup, setPopup] = useState(null); // aktif popup duyurusu
  const [shownIds, setShownIds] = useState(new Set()); // aynı oturumda tekrar açılmasın
  const knownIdsRef = useRef(new Set()); // ilk mount'ta eski duyurulara ses/bildirim gönderme
  const initialLoadedRef = useRef(false);

  // Bildirim izni: ilk defa duyuru geldiğinde kullanıcıdan izin isteme akışı
  useEffect(() => {
    if (!user || !canNotify()) return;
    const pref = localStorage.getItem(NOTIF_PREF_KEY);
    if (pref === "granted" || pref === "denied" || pref === "dismissed") return;
    if (Notification.permission === "granted") { localStorage.setItem(NOTIF_PREF_KEY, "granted"); return; }
    if (Notification.permission === "denied") { localStorage.setItem(NOTIF_PREF_KEY, "denied"); return; }
    // İzin isteme prompt'u (kullanıcı deneyimini bozmamak için toast ile)
    const t = setTimeout(() => {
      toast(
        <div className="flex items-start gap-2">
          <Bell className="h-4 w-4 mt-0.5 text-[#D95D39]" />
          <div className="flex-1">
            <div className="font-semibold text-sm">Anlık duyuru bildirimleri</div>
            <div className="text-xs text-slate-600 mt-0.5">
              Sekme kapalıyken bile kritik duyuruları anında görmek için izin verir misiniz?
            </div>
            <div className="mt-2 flex gap-2">
              <button
                onClick={async () => {
                  try {
                    const p = await Notification.requestPermission();
                    localStorage.setItem(NOTIF_PREF_KEY, p);
                    if (p === "granted") toast.success("Bildirimler açıldı");
                  } catch (_) {}
                }}
                className="px-2.5 py-1 rounded bg-[#D95D39] text-white text-xs hover:bg-[#C04E2E]"
              >İzin ver</button>
              <button
                onClick={() => localStorage.setItem(NOTIF_PREF_KEY, "dismissed")}
                className="px-2.5 py-1 rounded border border-slate-300 text-slate-600 text-xs"
              >Sonra</button>
            </div>
          </div>
        </div>,
        { duration: 12000, id: "notif-permission-prompt" }
      );
    }, 3000);
    return () => clearTimeout(t);
  }, [user]);

  const fetchActive = useCallback(async () => {
    if (!user) return;
    try {
      const r = await api.get("/announcements/active");
      const list = r.data || [];
      setItems(list);
      // Görülme kaydı — hem popup hem banner sayılır
      list.forEach((a) => {
        api.post(`/announcements/${a.id}/view`).catch(() => {});
      });

      // ── Yeni duyuruları tespit et → ses + tarayıcı bildirimi ──
      // İlk yüklemede ses çıkarma; sadece sonraki eklemeleri "yeni" say
      if (initialLoadedRef.current) {
        for (const a of list) {
          if (!knownIdsRef.current.has(a.id)) {
            // Ses çal
            playBeep(a.severity);
            // Sekme arka plandaysa tarayıcı bildirimi göster
            if (document.hidden || document.visibilityState === "hidden") {
              showBrowserNotification(a);
            }
          }
        }
      }
      knownIdsRef.current = new Set(list.map((a) => a.id));
      initialLoadedRef.current = true;

      // Sayfa yüklendiğinde henüz gösterilmemiş popup varsa göster
      const nextPopup = list.find((a) => a.show_as_popup && !shownIds.has(a.id));
      if (nextPopup) {
        setPopup(nextPopup);
        setShownIds((p) => new Set([...p, nextPopup.id]));
      }
    } catch { /* sessiz */ }
  }, [user, shownIds]);

  useEffect(() => {
    if (!user) return;
    fetchActive();

    // ── SSE: gerçek zamanlı olay dinleme ──
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    let es = null;
    let reconnectTimer = null;
    let stopped = false;

    const connect = () => {
      if (stopped) return;
      try {
        es = new EventSource(`${backendUrl}/api/announcements/stream`, { withCredentials: true });
      } catch (_) { return; }
      es.addEventListener("announcement", () => {
        // Herhangi bir olayda (create/update/delete) aktif listeyi tazele
        fetchActive();
      });
      es.addEventListener("ready", () => { /* bağlantı hazır */ });
      es.onerror = () => {
        // Bağlantı düştüyse 5 sn sonra yeniden dene
        try { es.close(); } catch (_) {}
        if (!stopped) {
          clearTimeout(reconnectTimer);
          reconnectTimer = setTimeout(connect, 5000);
        }
      };
    };
    connect();

    // Fallback polling — SSE düşerse 5 dakikada bir yakalar
    const pollId = setInterval(fetchActive, 5 * 60 * 1000);

    return () => {
      stopped = true;
      clearTimeout(reconnectTimer);
      clearInterval(pollId);
      if (es) { try { es.close(); } catch (_) {} }
    };
    // eslint-disable-next-line
  }, [user]);

  const dismiss = async (a) => {
    if (!a.dismissible) return;
    try {
      await api.post(`/announcements/${a.id}/dismiss`);
      setItems((p) => p.filter((x) => x.id !== a.id));
      setPopup(null);
    } catch { /* sessiz */ }
  };

  const closePopup = () => {
    // Sadece dismissible ise kapat + dismiss kaydı
    if (popup?.dismissible !== false) dismiss(popup);
    else setPopup(null);
  };

  // Banner'da gösterilecekler = popup dışında kalanlar (popup gösterilse bile banner'ı çıkarma)
  const bannerItems = items;

  if (!user) return null;

  return (
    <>
      {/* Üst bannerlar */}
      {bannerItems.length > 0 && (
        <div className="space-y-1.5 mb-4" data-testid="announcement-banners">
          {bannerItems.map((a) => {
            const s = SEV_STYLES[a.severity] || SEV_STYLES.info;
            const Icon = s.icon;
            return (
              <div key={a.id} className={`rounded-lg border px-3 py-2 flex items-start gap-2.5 ${s.banner}`} data-testid={`banner-${a.id}`}>
                <Icon className="h-4 w-4 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">{a.title}</div>
                  <div className="text-xs opacity-90 mt-0.5 whitespace-pre-line">{a.body}</div>
                </div>
                {a.dismissible !== false && (
                  <button
                    onClick={() => dismiss(a)}
                    className="shrink-0 p-1 rounded hover:bg-white/40 opacity-70 hover:opacity-100"
                    aria-label="Kapat"
                    data-testid={`dismiss-${a.id}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Popup Modal */}
      <Dialog open={!!popup} onOpenChange={(v) => { if (!v) closePopup(); }}>
        <DialogContent className="max-w-md" onInteractOutside={(e) => { if (popup?.dismissible === false) e.preventDefault(); }}>
          {popup && (() => {
            const s = SEV_STYLES[popup.severity] || SEV_STYLES.info;
            const Icon = s.icon;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Icon className={`h-5 w-5 ${s.ring}`} />
                    {popup.title}
                  </DialogTitle>
                </DialogHeader>
                <div className="text-sm text-slate-700 whitespace-pre-line py-2" data-testid="popup-body">
                  {popup.body}
                </div>
                <DialogFooter>
                  {popup.dismissible !== false ? (
                    <Button onClick={() => dismiss(popup)} className="bg-[#D95D39] hover:bg-[#C04E2E] text-white" data-testid="popup-dismiss">
                      Anladım, kapat
                    </Button>
                  ) : (
                    <div className="text-xs text-slate-500 italic w-full text-center">Bu duyuru kapatılamaz — yönetici tarafından pasifleştirilmesi gerekir.</div>
                  )}
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </>
  );
}
